/**
 * Product-specific state availability configuration
 */

import { UnitedStatesState } from './UnitedStatesState';

// Default available states
export const defaultAvailableStates = Object.entries(UnitedStatesState)
  .filter(([key]) => key !== 'none')
  .map(([, state]) => ({
    value: state.stateAbbreviation,
    label: state.stateName,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Product-specific state availability arrays
export const carescoutCareAssuranceStates = [
  'AK', 'AL', 'AR', 'AZ', 'CT', 'DC', 'DE', 'GA', 'IA', 'ID', 'IL',
  'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MO', 'MS', 'MT', 'WI',
  'NC', 'ND', 'NH', 'NM', 'NV', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WV', 'WY'
] as const;

// Nationwide CareMatters products (careMattersTwo and jointCareMatters have same availability)
// According to Claude, will update if Evan gives specific instructions
/*'MT' Not avalible yet according to Claude */
export const careMattersStates = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL',
  'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD',
  'ME', 'MI', 'MN', 'MO', 'MS', 'NC', 'ND', 'NE', 'NH', 'NJ',
  'NM', 'NV', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN',
  'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
] as const;

// OneAmerica AssetCare products (assetCare and jointAssetCare have same availability)
export const assetCareStates = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'MT', 'FL', 'GA', 'HI', 'IA',
  'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN',
  'MO', 'MS', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI',
  'WV', 'WY'
] as const;

export const secureCareThreeStates = [
  'DE', 'IN', 'MA', 'MT', 'ND', 'SC'
] as const

export const moneyGuardFixedAdvantageStates = [
  'AL', 'AK', 'AZ', 'AR', 'AZ', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'MA', 'ME', 'MD',
  'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'PR', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
] as const

// Mapping of funding policy types to their available states
export const PRODUCT_STATE_AVAILABILITY: Record<number, readonly string[]> = {
  2936: carescoutCareAssuranceStates, // FundingPolicyType.careScoutCareAssurance
  2919: careMattersStates, // FundingPolicyType.careMattersTwo
  2921: careMattersStates, // FundingPolicyType.jointCareMatters
  2918: assetCareStates, // FundingPolicyType.assetCare
  2920: assetCareStates, // FundingPolicyType.jointAssetCare
  2916: secureCareThreeStates, // FundingPolicyType.secureCareThree
  2917: moneyGuardFixedAdvantageStates, // FundingPolicyType.moneyGuardFixedAdvantage
} as const;

// Helper function to get available states for a funding policy type
export function getProductAvailableStates(
  fundingPolicyType: number,
): readonly string[] | undefined {
  return PRODUCT_STATE_AVAILABILITY[fundingPolicyType];
}

// Helper function to check if a product has specific state availability
export function hasProductSpecificStateAvailability(fundingPolicyType: number): boolean {
  return fundingPolicyType in PRODUCT_STATE_AVAILABILITY;
}

// Legacy export for backward compatibility (ShopInsuranceModal still uses this)
export const carescoutAvailableStates = carescoutCareAssuranceStates;

// States that are universally excluded (not approved to sell)
export const EXCLUDED_STATES = [] as const;

// OneAmerica expanded states: Category 2 states (except MT) that have been moved to Category 1
// These states are gated behind the oneAmericaExpandedStates feature flag
export const OA_EXPANDED_STATES = ['CT', 'DE', 'DC', 'ND', 'SC', 'SD'] as const;

// Get all states that have at least one product available
export function getAllSupportedStates(): string[] {
  const allStates = new Set<string>([
    ...carescoutCareAssuranceStates,
    ...careMattersStates,
    ...assetCareStates,
    ...secureCareThreeStates,
    ...moneyGuardFixedAdvantageStates,
  ]);
  return Array.from(allStates).sort();
}

// Check if a state has any quote products available
export function isStateSupported(stateAbbreviation: string): boolean {
  return getAllSupportedStates().includes(stateAbbreviation);
}
